/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import React from 'react';
import PropTypes from 'prop-types';

import { Asset } from '@react/react-spectrum/Asset';
import AlertIcon from '@spectrum-icons/workflow/AlertCircle';
import { Card, CardBody, CardPreview } from '@react/react-spectrum/Card';
import { defineMessages, useIntl } from 'react-intl';

import classes from './Cards.scss';

const messages = defineMessages({
    subtitleConfiguration: {
        id: 'subtitle.configuration',
        defaultMessage: 'Configuration'
    }
});

const ConfigCard = React.forwardRef((props, ref) => {
    const { config, errorMessage, ...otherProps } = props;
    const { name, storeView, schemaVersion, identifier } = config;
    const { formatMessage } = useIntl();

    const className = props.onClick ? classes.clickable : '';
    const displayName = name !== 'Commerce' ? name : identifier.split('-').join(' ');

    const cardPreview = !errorMessage ? (
        <Asset type="folder" alt={name} />
    ) : (
        <div className={classes['alert-icon__container']}>
            <AlertIcon />
            <p>{errorMessage}</p>
        </div>
    );

    let description = '';

    if (storeView && schemaVersion) {
        description = `${storeView} / ${schemaVersion}`;
    } else if (storeView) {
        description = storeView;
    } else if (schemaVersion) {
        description = schemaVersion;
    }

    return (
        <Card {...otherProps} forwardedRef={ref} className={className}>
            <CardPreview>{cardPreview}</CardPreview>
            <CardBody
                title={displayName}
                subtitle={formatMessage(messages.subtitleConfiguration)}
                description={description}
            />
        </Card>
    );
});

ConfigCard.displayName = 'ConfigCard';

ConfigCard.propTypes = {
    errorMessage: PropTypes.string,
    onClick: PropTypes.func,
    config: PropTypes.shape({
        name: PropTypes.string,
        storeView: PropTypes.string,
        identifier: PropTypes.string,
        schemaVersion: PropTypes.string
    })
};

export default ConfigCard;
